/**
 * Chat Message Service
 * Handles persistence of chat messages to Firestore
 *
 * Phase 2 additions:
 * - markMessagesResolved: Batch update messages with resolution data
 * - updateChannelActiveCount: Set channel's activeMessageCount
 * - incrementChannelActiveCount: Increment active count for new messages
 * - ensureChannelExists: Create/get channel with deterministic ID
 */

import {
  collection,
  addDoc,
  doc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  increment,
  arrayUnion,
  writeBatch,
  type Timestamp,
  type Unsubscribe
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { log } from '@/lib/logger';
import type { ChatMessage } from '@/shared/stores/commentStore';
import { NotificationService, type MentionData } from '@/domains/billing/services/shared/notificationService';
// Import types from chatTypes.ts
import type { ChatMessageDocument, ChatChannelInstance } from './chatTypes';


// Import extracted functions
import { addReaction as addReactionFn, removeReaction as removeReactionFn } from './reactionOperations';
import {
  reactivateChannel as reactivateChannelFn,
  getArchivedMessages as getArchivedMessagesFn,
  getArchivedChannels as getArchivedChannelsFn,
  getActiveChannelForEntry as getActiveChannelForEntryFn,
  getAllChannelsForEntry as getAllChannelsForEntryFn,
} from './channelQueryOperations';

export class ChatMessageService {
  private static readonly MESSAGES_COLLECTION = 'chat_messages';
  private static readonly CHANNELS_COLLECTION = 'chat_channels';

  /**
   * Save a chat message to Firestore with duplicate prevention
   */
  static async saveMessage(
    channelId: string,
    workflowId: string,
    entryId: string,
    message: ChatMessage,
    workflowName?: string,
    entryDescription?: string,
    pinContext?: {
      pageUrl: string;
      scrollY: number;
      pinId: string;
    }
  ): Promise<void> {
    try {
      const messagesRef = collection(db, this.MESSAGES_COLLECTION);

      // Check for existing message with same content and client within last 5 seconds
      const recentTimeThreshold = new Date(Date.now() - 5000); // 5 seconds ago
      const duplicateQuery = query(
        messagesRef,
        where('channelId', '==', channelId),
        where('clientId', '==', message.clientId),
        where('text', '==', message.text),
        where('createdAt', '>', recentTimeThreshold)
      );

      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        return; // Skip saving duplicate
      }

      const messageDoc: Omit<ChatMessageDocument, 'id'> = {
        channelId,
        workflowId,
        entryId,
        text: message.text,
        clientId: message.clientId,
        metadata: {
          ...message.metadata,
          timestamp: new Date().toISOString(),
        },
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
      };


      // Save the message
      const docRef = await addDoc(messagesRef, messageDoc);

      // Update or create channel instance
      await this.updateChannelInstance(
        channelId,
        workflowId,
        entryId,
        message.clientId,
        workflowName,
        entryDescription
      );

      // Process mention notifications after successful message save
      try {
        // Only check for mentions if message contains @
        if (message.text.includes('@')) {

          // Use clientId (employee_id) for mentionedBy, not display name
          const currentUserEmployeeId = message.clientId;

          const mentionData: MentionData = {
            messageId: docRef.id,
            channelId,
            workflowId,
            entryId,
            mentionedBy: currentUserEmployeeId,
            messageText: message.text,
            messageTimestamp: new Date(),
            workflowName,
            entryDescription,
            // Include pin context for cross-domain comment deep linking
            ...(pinContext && {
              pageUrl: pinContext.pageUrl,
              scrollY: pinContext.scrollY,
              pinId: pinContext.pinId
            })
          };

          await NotificationService.processMentionNotification(mentionData);
        }
      } catch (error) {
        log.error('Failed to create mention notifications:', error);
        // Don't throw - message was saved successfully
      }

    } catch (error) {
      log.error('Error saving chat message:', error);
      throw error;
    }
  }

  /**
   * Load chat messages for a channel
   *
   * By default, excludes resolved messages (conversations that have been closed).
   * Set `includeResolved` to true to load all messages including resolved ones
   * (useful for audit/history purposes).
   *
   * Note: Uses client-side filtering to handle legacy messages that don't have
   * the `isResolved` field. Messages without this field are treated as NOT resolved.
   *
   * @param channelId - The channel ID to load messages for
   * @param limitCount - Maximum number of messages to load (default: 50)
   * @param includeResolved - If true, includes resolved messages (default: false)
   * @returns Array of chat messages in chronological order (oldest first)
   */
  static async loadMessages(
    channelId: string,
    limitCount: number = 50,
    includeResolved: boolean = false
  ): Promise<ChatMessage[]> {
    try {
      const messagesRef = collection(db, this.MESSAGES_COLLECTION);
      const q = query(
        messagesRef,
        where('channelId', '==', channelId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const messages: ChatMessage[] = [];

      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data() as ChatMessageDocument;

        // Filter out resolved messages unless includeResolved is true
        // Legacy messages without isResolved field are treated as NOT resolved
        if (!includeResolved && data.isResolved === true) {
          return; // Skip resolved messages
        }

        const message: ChatMessage = {
          id: docSnapshot.id,
          text: data.text,
          createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
          clientId: data.clientId,
          metadata: data.metadata,
          reactions: data.reactions || {},
        };
        messages.push(message);
      });

      // Return in chronological order (oldest first)
      return messages.reverse();

    } catch (error) {
      log.error('Error loading chat messages:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time chat messages for a channel
   *
   * By default, excludes resolved messages (conversations that have been closed).
   * Set `includeResolved` to true to subscribe to all messages including resolved ones.
   *
   * Note: Uses client-side filtering to handle legacy messages that don't have
   * the `isResolved` field. Messages without this field are treated as NOT resolved.
   *
   * @param channelId - The channel ID to subscribe to
   * @param callback - Callback function called with messages array when changes occur
   * @param limitCount - Maximum number of messages to track (default: 50)
   * @param includeResolved - If true, includes resolved messages (default: false)
   * @returns Unsubscribe function to stop listening
   */
  static subscribeToMessages(
    channelId: string,
    callback: (messages: ChatMessage[]) => void,
    limitCount: number = 50,
    includeResolved: boolean = false
  ): Unsubscribe {
    const messagesRef = collection(db, this.MESSAGES_COLLECTION);
    const q = query(
      messagesRef,
      where('channelId', '==', channelId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(q, (snapshot) => {
      const messages: ChatMessage[] = [];

      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data() as ChatMessageDocument;

        // Filter out resolved messages unless includeResolved is true
        // Legacy messages without isResolved field are treated as NOT resolved
        if (!includeResolved && data.isResolved === true) {
          return; // Skip resolved messages
        }

        const message: ChatMessage = {
          id: docSnapshot.id,
          text: data.text,
          createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
          clientId: data.clientId,
          metadata: data.metadata,
          reactions: data.reactions || {},
        };
        messages.push(message);
      });

      // Return in chronological order (oldest first)
      callback(messages.reverse());
    }, (error) => {
      log.error('Error in chat messages subscription:', error);
    });
  }

  /**
   * Update or create channel instance
   */
  private static async updateChannelInstance(
    channelId: string,
    workflowId: string,
    entryId: string,
    clientId: string,
    workflowName?: string,
    entryDescription?: string
  ): Promise<void> {
    try {
      const channelDocRef = doc(db, this.CHANNELS_COLLECTION, channelId);
      const existing = await getDoc(channelDocRef);

      if (!existing.exists()) {
        // Create new channel instance
        // Extract sequence number from channelId for new sequential approach
        const sequenceMatch = channelId.match(/-(\d+)$/);
        const sequenceNumber = sequenceMatch ? parseInt(sequenceMatch[1]) : 1;
        const baseChannelId = channelId.replace(/-\d+$/, '');

        const channelDoc: Omit<ChatChannelInstance, 'id'> = {
          channelId,
          baseChannelId,
          sequenceNumber,
          workflowId,
          entryId,
          workflowName: workflowName || 'Unknown Workflow',
          entryDescription: entryDescription || 'Unknown Entry',
          isActive: true,
          isArchived: false,
          messageCount: 1,
          lastMessageAt: serverTimestamp() as Timestamp,
          participants: [clientId],
          createdAt: serverTimestamp() as Timestamp,
          updatedAt: serverTimestamp() as Timestamp,
        };

        await setDoc(doc(db, this.CHANNELS_COLLECTION, channelId), channelDoc);
      } else {
        await updateDoc(channelDocRef, {
          messageCount: increment(1),
          activeMessageCount: increment(1),
          totalMessageCount: increment(1),
          lastMessageAt: serverTimestamp(),
          participants: arrayUnion(clientId),
          isActive: true,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      log.error('Error updating channel instance:', error);
      // Don't throw here - message saving is more important than channel tracking
    }
  }

  /**
   * Get channel instance information
   */
  static async getChannelInstance(channelId: string): Promise<ChatChannelInstance | null> {
    try {
      const docSnapshot = await getDoc(doc(db, this.CHANNELS_COLLECTION, channelId));

      if (!docSnapshot.exists()) {
        return null;
      }

      return {
        id: docSnapshot.id,
        ...docSnapshot.data()
      } as ChatChannelInstance;

    } catch (error) {
      log.error('Error getting channel instance:', error);
      return null;
    }
  }

  // ============================================================================
  // Delegated methods from extracted modules (for backward compatibility)
  // ============================================================================

  /**
   * Reactivate an archived channel for new conversations
   * This allows users to start new conversations on previously resolved rows
   */
  static reactivateChannel = reactivateChannelFn;

  /**
   * Get archived messages for a channel
   */
  static getArchivedMessages = getArchivedMessagesFn;

  /**
   * Get all archived channels for a workflow
   */
  static getArchivedChannels = getArchivedChannelsFn;

  /**
   * Get the most recent active channel for a specific entry
   * This handles the case where multiple channels might exist for the same row
   */
  static getActiveChannelForEntry = getActiveChannelForEntryFn;

  /**
   * Get all channels (active and archived) for a specific entry
   * Useful for debugging or admin purposes
   */
  static getAllChannelsForEntry = getAllChannelsForEntryFn;

  /**
   * Add a reaction to a message
   * @param messageId - The Firestore document ID of the message
   * @param emoji - The emoji to add
   * @param clientId - The user's client ID (employee ID)
   */
  static addReaction = addReactionFn;

  /**
   * Remove a reaction from a message
   * @param messageId - The Firestore document ID of the message
   * @param emoji - The emoji to remove
   * @param clientId - The user's client ID (employee ID)
   */
  static removeReaction = removeReactionFn;

  // ============================================================================
  // Phase 2 Methods - Message Resolution and Deterministic Channel Management
  // ============================================================================

  /**
   * Mark all messages in a channel as resolved
   * Uses batch write for atomic operation
   *
   * @param channelId - The channel ID to resolve
   * @param resolvedBy - The user ID who resolved the conversation
   */
  static async markMessagesResolved(
    channelId: string,
    resolvedBy: string
  ): Promise<void> {
    try {

      // Get all messages for this channel
      const messagesRef = collection(db, this.MESSAGES_COLLECTION);
      const q = query(
        messagesRef,
        where('channelId', '==', channelId)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return;
      }

      // Use batch write for atomic updates
      const batch = writeBatch(db);
      const resolvedAt = serverTimestamp();

      snapshot.docs.forEach((docSnapshot) => {
        batch.update(docSnapshot.ref, {
          isResolved: true,
          resolvedAt,
          resolvedBy,
          updatedAt: resolvedAt,
        });
      });

      await batch.commit();


    } catch (error) {
      log.error('Error marking messages as resolved:', error);
      throw error;
    }
  }

  /**
   * Update channel's activeMessageCount to a specific value
   * Used when resolving (set to 0) or syncing counts
   *
   * @param channelId - The channel ID to update
   * @param count - The new active message count
   */
  static async updateChannelActiveCount(
    channelId: string,
    count: number
  ): Promise<void> {
    try {

      const channelDocRef = doc(db, this.CHANNELS_COLLECTION, channelId);
      const channelSnap = await getDoc(channelDocRef);

      if (!channelSnap.exists()) {
        log.warn(`[CHAT-SERVICE] Channel not found for active count update: ${channelId}`);
        return;
      }

      await updateDoc(channelDocRef, {
        activeMessageCount: count,
        isActive: count > 0,
        updatedAt: serverTimestamp(),
      });


    } catch (error) {
      log.error('Error updating channel active count:', error);
      throw error;
    }
  }

  /**
   * Increment channel's activeMessageCount (for new messages)
   * Used when a new message is sent
   *
   * @param channelId - The channel ID to increment
   */
  static async incrementChannelActiveCount(channelId: string): Promise<void> {
    try {
      const channelDocRef = doc(db, this.CHANNELS_COLLECTION, channelId);
      const channelSnap = await getDoc(channelDocRef);

      if (!channelSnap.exists()) {
        log.warn(`[CHAT-SERVICE] Channel not found for increment: ${channelId}`);
        return;
      }

      await updateDoc(channelDocRef, {
        activeMessageCount: increment(1),
        totalMessageCount: increment(1),
        messageCount: increment(1),
        isActive: true,
        updatedAt: serverTimestamp(),
      });

    } catch (error) {
      log.error('Error incrementing channel active count:', error);
      // Don't throw - this is a non-critical operation
    }
  }

  /**
   * Get or create a channel using a deterministic ID
   * No sequential lookup - uses the provided channelId directly
   *
   * This is the Phase 2 approach: deterministic IDs eliminate race conditions
   *
   * @param channelId - The deterministic channel ID
   * @param workflowId - The workflow ID
   * @param entryId - The entry ID (for countMap lookup)
   * @param metadata - Optional metadata for the channel
   * @returns The channel instance (existing or newly created)
   */
  static async ensureChannelExists(
    channelId: string,
    workflowId: string,
    entryId: string,
    metadata?: {
      workflowName?: string;
      entryDescription?: string;
    }
  ): Promise<ChatChannelInstance> {
    try {

      // Check if channel already exists
      const existing = await this.getChannelInstance(channelId);

      if (existing) {
        return existing;
      }

      // Create new channel with deterministic ID
      // Use document ID as the channelId for direct lookup
      const channelDocRef = doc(db, this.CHANNELS_COLLECTION, channelId);

      const now = serverTimestamp();
      const channelData: Omit<ChatChannelInstance, 'id'> = {
        channelId,
        baseChannelId: channelId, // For deterministic IDs, base equals full ID
        sequenceNumber: 1, // Not used in deterministic approach
        workflowId,
        entryId,
        workflowName: metadata?.workflowName || 'Unknown Workflow',
        entryDescription: metadata?.entryDescription || 'Unknown Entry',
        isActive: true,
        isArchived: false,
        messageCount: 0,
        // Phase 2 fields
        participants: [],
        createdAt: now as Timestamp,
        updatedAt: now as Timestamp,
      };

      // Create with merge to handle race conditions
      await setDoc(channelDocRef, channelData, { merge: true });


      // Return the created channel
      return {
        id: channelId,
        ...channelData,
        createdAt: { seconds: Date.now() / 1000, nanoseconds: 0 } as Timestamp,
        updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 } as Timestamp,
      };

    } catch (error) {
      log.error('Error ensuring channel exists:', error);
      throw error;
    }
  }

}
